/**
 * `$snotify` shim — translates the Vue-Snotify call surface the codebase
 * uses to vue-toastification calls. Keeps the 25 mixin-driven `spawnXToast`
 * sites + the 5 direct `this.$snotify.info(...)` sites in dialogue.vue
 * working without touching any of them.
 *
 * vue-snotify API (what we shim):
 *   $snotify.success(message, title, opts)
 *   $snotify.error  (message, title, opts)
 *   $snotify.warning(message, title, opts)
 *   $snotify.info   (message, title, opts)
 *
 * vue-toastification API (what we call):
 *   $toast.success(content, opts)
 *   $toast.error  (content, opts)
 *   $toast.warning(content, opts)
 *   $toast.info   (content, opts)
 *
 * `title` is folded into `content` as a plain string (vue-toastification
 * also supports HTML / component content; not needed here). Position /
 * timeout / closeOnClick map to vue-toastification's option names.
 *
 * Phase 6: install(app) instead of install(Vue). vue-toastification@2 uses
 * `App.provide(toastInjectionKey, ...)` — Vue 3 native — so we register it
 * via `app.use(Toast, ...)` and read the bus-bound interface via `useToast()`
 * (which falls back to the global event bus when called outside of setup
 * context).
 */
import Toast, { POSITION, useToast } from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import store from '@/store/mainStore'
import { uiToastsEnabled } from '@/utils/uiToasts'

// Snotify's position enum mapped to vue-toastification's POSITION enum.
// Only the values the mixin ever uses (`leftBottom` is the only one).
const POSITION_MAP = {
    leftTop:     POSITION.TOP_LEFT,
    leftCenter:  POSITION.TOP_CENTER,
    leftBottom:  POSITION.BOTTOM_LEFT,
    rightTop:    POSITION.TOP_RIGHT,
    rightCenter: POSITION.TOP_CENTER,
    rightBottom: POSITION.BOTTOM_RIGHT,
    centerTop:   POSITION.TOP_CENTER,
    centerCenter:POSITION.TOP_CENTER,
    centerBottom:POSITION.BOTTOM_CENTER,
}

function translateOptions(snotifyOpts = {}) {
    const out = {}
    if (snotifyOpts.timeout != null) out.timeout = snotifyOpts.timeout
    if (snotifyOpts.closeOnClick != null) out.closeOnClick = snotifyOpts.closeOnClick
    if (snotifyOpts.pauseOnHover != null) out.pauseOnHover = snotifyOpts.pauseOnHover
    if (snotifyOpts.showProgressBar != null) out.hideProgressBar = !snotifyOpts.showProgressBar
    if (snotifyOpts.position && POSITION_MAP[snotifyOpts.position]) {
        out.position = POSITION_MAP[snotifyOpts.position]
    }
    return out
}

function buildContent(message, title) {
    // Snotify shows title above message; vue-toastification accepts a
    // single string. Concatenating with a newline gives a similar visual.
    if (title && message) return `${title}\n${message}`
    return title || message || ''
}

// `success` and `info` are confirmations and announcements — they land in the
// same bottom-left corner as LearnUToast and are covered by the same per-user
// opt-out. Gating here rather than at the call sites is deliberate: the
// realtime toasts in dialogue.vue ("A student submitted an assignment") were
// missed when the setting shipped because they were assumed to appear in a
// different corner, and they do not — no call site passes a position, so they
// inherit the BOTTOM_LEFT default below. `error` and `warning` always show.
function shim(toast, kind) {
    const suppressable = kind === 'success' || kind === 'info'
    return (message, title, opts) => {
        if (suppressable && !uiToastsEnabled(store.state.userStore?.user_settings)) return
        return toast[kind](buildContent(message, title), translateOptions(opts))
    }
}

export default {
    install(app) {
        app.use(Toast, {
            position: POSITION.BOTTOM_LEFT,
            timeout: 1500,
            closeOnClick: true,
            pauseOnHover: true,
            showCloseButtonOnHover: false,
            hideProgressBar: true,
            closeButton: 'button',
            icon: true,
            rtl: false,
        })

        // useToast() outside of setup context returns
        // `createToastInterface(globalEventBus)` — same bus the plugin
        // installed against above, so the interface is live.
        const toast = useToast()

        app.config.globalProperties.$toast = toast
        app.config.globalProperties.$snotify = {
            success: shim(toast, 'success'),
            error:   shim(toast, 'error'),
            warning: shim(toast, 'warning'),
            info:    shim(toast, 'info'),
        }
    },
}
